/**
 * Every failure the admin panel can show a person, as one type.
 *
 * The API answers every non-2xx with the `errorEnvelope` from `@wend/shared`:
 * a stable `code`, a message that is already safe to display, optional
 * `fieldErrors`, and a `requestId` that correlates with a server log line. This
 * module turns that envelope — or a transport failure, or a response that does
 * not match its contract — into a single `ApiError` the UI can switch on.
 */
import { errorEnvelopeSchema, type ErrorCode, type FieldError } from '@wend/shared';

export interface ApiErrorInit {
  readonly code: ErrorCode;
  readonly message: string;
  /** HTTP status, or 0 when the request never reached the server. */
  readonly status: number;
  readonly fieldErrors?: readonly FieldError[];
  readonly requestId?: string;
  readonly retryAfterSeconds?: number;
  readonly cause?: unknown;
}

/** Fallbacks for a response that failed without a usable envelope. */
const GENERIC_MESSAGES: Readonly<Record<ErrorCode, string>> = {
  bad_request: 'The request could not be processed.',
  validation_failed: 'The request could not be validated.',
  unauthorized: 'Your session has expired. Please sign in again.',
  forbidden: 'You do not have access to this resource.',
  not_found: 'Not found.',
  conflict: 'That conflicts with something that already exists.',
  rate_limited: 'Too many attempts. Please wait a moment and try again.',
  internal: 'Something went wrong. Please try again.',
};

function codeForStatus(status: number): ErrorCode {
  switch (status) {
    case 400:
      return 'bad_request';
    case 401:
      return 'unauthorized';
    case 403:
      return 'forbidden';
    case 404:
      return 'not_found';
    case 409:
      return 'conflict';
    case 429:
      return 'rate_limited';
    default:
      return status >= 500 ? 'internal' : 'bad_request';
  }
}

async function readJsonBody(response: Response): Promise<unknown> {
  try {
    const text = await response.text();
    if (text === '') return undefined;
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}

function readRetryAfter(response: Response): number | undefined {
  const header = response.headers.get('Retry-After');
  if (header === null) return undefined;
  const seconds = Number.parseInt(header, 10);
  return Number.isFinite(seconds) && seconds > 0 ? seconds : undefined;
}

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly fieldErrors: readonly FieldError[];
  readonly requestId: string | undefined;
  readonly retryAfterSeconds: number | undefined;

  constructor(init: ApiErrorInit) {
    super(init.message, init.cause === undefined ? undefined : { cause: init.cause });
    this.name = 'ApiError';
    this.code = init.code;
    this.status = init.status;
    this.fieldErrors = init.fieldErrors ?? [];
    this.requestId = init.requestId;
    this.retryAfterSeconds = init.retryAfterSeconds;
  }

  /** True when the request never reached the API at all. */
  get isNetworkError(): boolean {
    return this.status === 0;
  }

  get isUnauthorized(): boolean {
    return this.status === 401 || this.code === 'unauthorized';
  }

  /** The browser could not make the request: API down, wrong URL, CORS. */
  static network(cause: unknown): ApiError {
    return new ApiError({
      code: 'internal',
      status: 0,
      message: 'Could not reach the Wend API.',
      cause,
    });
  }

  /** A 2xx body that does not match the contract in `@wend/shared`. */
  static unexpectedResponse(status: number): ApiError {
    return new ApiError({
      code: 'internal',
      status,
      message: 'The API returned a response this app does not understand.',
    });
  }

  static unauthorized(message = GENERIC_MESSAGES.unauthorized): ApiError {
    return new ApiError({ code: 'unauthorized', status: 401, message });
  }

  /** Reads the shared error envelope off a failed response. */
  static async fromResponse(response: Response): Promise<ApiError> {
    const retryAfterSeconds = readRetryAfter(response);
    const parsed = errorEnvelopeSchema.safeParse(await readJsonBody(response));

    if (parsed.success) {
      const { code, message, fieldErrors, requestId } = parsed.data.error;
      return new ApiError({
        code,
        message,
        status: response.status,
        fieldErrors: fieldErrors ?? [],
        requestId,
        retryAfterSeconds,
      });
    }

    const code = codeForStatus(response.status);
    return new ApiError({
      code,
      status: response.status,
      message: GENERIC_MESSAGES[code],
      retryAfterSeconds,
    });
  }
}

/** True for the DOMException `fetch` throws when its signal is aborted. */
export function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

/** Narrows anything thrown by the API client — a bug included — to an ApiError. */
export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;
  return new ApiError({
    code: 'internal',
    status: 0,
    message: GENERIC_MESSAGES.internal,
    cause: error,
  });
}

/** The sentence to show a person. Transport failures get a useful hint. */
export function describeApiError(error: ApiError): string {
  if (error.isNetworkError) {
    return 'Could not reach the Wend API. Check that it is running, then try again.';
  }
  if (error.code === 'rate_limited' && error.retryAfterSeconds !== undefined) {
    return `${error.message} Try again in ${error.retryAfterSeconds} seconds.`;
  }
  return error.message;
}
